import React, {
  useState,
  useCallback,
  useMemo,
} from 'react';
import { Button, Flex, Popover } from 'antd';
import debounce from 'lodash.debounce';
import { TagSelect } from './TagSelect';
import { Tag, TagType } from '../Tag';
import cn from './styles.module.scss';

type EditableTagGroupProps = {
  tags: TagType[];
  onChange: (tags: string[]) => void;
  title?: string;
  onSearch?: (value: string) => Promise<TagType[]>;
  onError?: (errorMessage: string, e: Error) => void;
  disabled?: boolean,
  children?: React.ReactNode,
};

export const EditableTagGroup = ({
  tags, onChange, onSearch, onError, disabled, title, children,
}: EditableTagGroupProps) => {
  const [searchResult, setSearchResult] = useState<TagType[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);

  const editableTags = useMemo(() => tags.filter(({ editable }) => editable !== false), [tags]);

  const handleClose = (removedTag: number) => {
    if (onChange) {
      onChange(editableTags.filter((tag) => tag.id !== removedTag).map((tag) => tag.name));
    }
  };

  const setSearchResultWithoutCurrent = useCallback((resultTags: TagType[]) => {
    setSearchResult(
      resultTags.filter((resultTag: TagType) => !tags.find((tag) => tag.id === resultTag.id)),
    );
  }, [tags]);

  const search = useCallback(async (value: string) => {
    if (onSearch) {
      try {
        setLoading(true);
        const resultTags = await onSearch(value);
        setSearchResultWithoutCurrent(resultTags);
      } catch (e: any) {
        if (onError) {
          onError('Search tag error', e);
        }
      }

      setLoading(false);
    }
  }, [setSearchResultWithoutCurrent]);

  const debouncedSearch = useCallback(
    debounce((value) => search(value), 500),
    [search],
  );

  const handleSearch = async (value: string) => {
    setInputValue(value);
    debouncedSearch(value);
  };

  const handleVisibleChange = (visible: boolean) => {
    if (visible) {
      handleSearch('');
    } else {
      setSearchResult([]);
      setInputValue('');
    }
    setOpen(visible);
  };

  if (disabled) {
    return (
      <div className={cn.container}>
        {tags.length > 0 && tags.map((tag) => (
          <Tag
            key={tag.id}
            tag={tag}
            maxLength={8}
            onClose={tag.editable && !disabled ? handleClose : undefined}
            data-testid={`tag_${tag.name}`}
          />
        ))}
      </div>
    );
  }

  return (
    <Popover
      trigger="click"
      destroyOnHidden
      onOpenChange={handleVisibleChange}
      open={open}
      classNames={{
        root: cn.overlay
      }}
      title={<span className={cn.title}>{title || 'Tags'}</span>}
      content={(
        <>
          <TagSelect
            onChange={onChange}
            tags={editableTags}
            loading={loading}
            handleClose={handleClose}
            handleSearch={handleSearch}
            inputValue={inputValue}
            searchResult={searchResult}
            closeContainer={() => setOpen(false)}
          />
          {children}
        </>
      )}
    >
      <div className={cn.container}>
        {tags.length > 0
          ? <Flex gap="small" align="center" wrap>
              {tags.map((tag) => (
                <Tag
                  key={tag.id}
                  tag={tag}
                  maxLength={8}
                  onClose={tag.editable ? handleClose : undefined}
                  data-testid={`tag_${tag.name}`}
                />
              ))}
            </Flex>
          : (
            <Button
              color="primary"
              variant="outlined"
              size="middle"
              data-testid="addTagButton"
            >
              Add Tag
            </Button>
          )}
      </div>
    </Popover>
  );
};

export default EditableTagGroup;
